import React, { useState } from 'react';
import { ChevronLeft, Edit2, BarChart2, ArrowRightLeft, Calendar, Bug, Info, Dumbbell, CheckCircle2, Check } from 'lucide-react';
import { Button } from '../components/Button';
import { ReplaceExerciseSheet } from '../components/ReplaceExerciseSheet';
import { CorrectExerciseSheet } from '../components/CorrectExerciseSheet';
import { CompleteWorkoutModal } from '../components/CompleteWorkoutModal';
import { ChangeTargetDateModal } from '../components/ChangeTargetDateModal';
interface WorkoutSessionProps {
  onBack: () => void;
  onComplete: () => void;
  onViewAnalytics: (exercise: string) => void;
  isFutureWorkout?: boolean;
  isCompletedWorkout?: boolean;
}
export function WorkoutSession({
  onBack,
  onComplete,
  onViewAnalytics,
  isFutureWorkout = false,
  isCompletedWorkout = false
}: WorkoutSessionProps) {
  const [replacingExercise, setReplacingExercise] = useState<string | null>(null);
  const [correctingExercise, setCorrectingExercise] = useState<string | null>(null);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showChangeDateModal, setShowChangeDateModal] = useState(false);
  const [skippedExercise, setSkippedExercise] = useState<string | null>(null);
  const [showSkipToast, setShowSkipToast] = useState(false);
  // Debug state
  const [showDebug, setShowDebug] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const [isMissed, setIsMissed] = useState(false);
  const [showFutureBanner, setShowFutureBanner] = useState(isFutureWorkout);
  const [showCompletedBanner, setShowCompletedBanner] = useState(isCompletedWorkout);
  const isFuturePreview = showFutureBanner && !isCancelled && !isMissed && !isCompletedWorkout;
  const isReadOnly = isCancelled || isMissed || isCompletedWorkout || showCompletedBanner || isFuturePreview;
  const [exercises, setExercises] = useState([{
    id: 1,
    name: 'Barbell Squat',
    weight: '60 kg',
    sets: 3,
    reps: '8-10',
    rest: '90s',
    completedWeight: '62.5 kg',
    completedSets: 3,
    completedReps: '10, 9, 8'
  }, {
    id: 2,
    name: 'Dumbbell Bench Press',
    weight: '20 kg',
    sets: 3,
    reps: '10-12',
    rest: '90s',
    completedWeight: '20 kg',
    completedSets: 3,
    completedReps: '12, 11, 10'
  }, {
    id: 3,
    name: 'Lat Pulldown',
    weight: '45 kg',
    sets: 3,
    reps: '10-12',
    rest: '90s',
    completedWeight: '47.5 kg',
    completedSets: 3,
    completedReps: '11, 10, 10'
  }, {
    id: 4,
    name: 'Romanian Deadlift',
    weight: '50 kg',
    sets: 3,
    reps: '10-12',
    rest: '90s',
    completedWeight: '50 kg',
    completedSets: 3,
    completedReps: '12, 11, 10'
  }]);
  const handleReplace = (oldName: string, newName: string) => {
    setExercises(exercises.map(ex => ex.name === oldName ? {
      ...ex,
      name: newName
    } : ex));
    setReplacingExercise(null);
  };
  const handleSkipExercise = (exerciseName: string) => {
    setExercises(exercises.filter(ex => ex.name !== exerciseName));
    setCorrectingExercise(null);
    setSkippedExercise(exerciseName);
    setShowSkipToast(true);
    // Auto-hide toast after 5 seconds
    setTimeout(() => {
      setShowSkipToast(false);
    }, 5000);
  };
  const handleUndoSkip = () => {
    if (skippedExercise) {
      // Re-add the skipped exercise (simplified - in real app would restore full state)
      const exerciseData = {
        id: Date.now(),
        name: skippedExercise,
        weight: '60 kg',
        sets: 3,
        reps: '8-10',
        rest: '90s'
      };
      setExercises([...exercises, exerciseData]);
      setSkippedExercise(null);
      setShowSkipToast(false);
    }
  };
  return <div className="flex flex-col h-full w-full bg-stone-50 overflow-hidden relative" data-id="element-1244">
      {/* Debug Toggle Button */}
      <button onClick={() => setShowDebug(!showDebug)} className="absolute top-8 right-2 z-40 w-7 h-7 rounded-full bg-slate-800/70 text-white flex items-center justify-center hover:bg-slate-800 transition-colors" title="Debug panel" data-id="element-1245">
        <Bug size={14} data-id="element-1246" />
      </button>

      {/* Debug Panel */}
      {showDebug && <div className="absolute top-16 right-2 z-40 bg-slate-800 text-white rounded-2xl p-3 shadow-xl w-64 text-xs animate-in fade-in zoom-in-95 duration-150 max-h-[500px] overflow-y-auto" data-id="element-1247">
          <p className="font-bold text-slate-400 uppercase tracking-wider text-[10px] mb-2" data-id="element-1248">
            Debug Panel
          </p>
          <div className="flex items-center justify-between px-2 py-1.5" data-id="element-1249">
            <span className="text-slate-300 font-medium" data-id="element-1250">Cancelled state</span>
            <button onClick={() => {
          const next = !isCancelled;
          setIsCancelled(next);
          if (next) {
            setIsMissed(false);
            setShowCompletedBanner(false);
          }
          setShowDebug(false);
        }} className={`w-9 h-5 rounded-full transition-colors relative ${isCancelled ? 'bg-emerald-500' : 'bg-slate-600'}`} data-id="element-1251">
              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${isCancelled ? 'translate-x-4' : 'translate-x-0.5'}`} data-id="element-1252" />
            </button>
          </div>
          <div className="flex items-center justify-between px-2 py-1.5" data-id="element-1253">
            <span className="text-slate-300 font-medium" data-id="element-1254">Missed state</span>
            <button onClick={() => {
          const next = !isMissed;
          setIsMissed(next);
          if (next) {
            setIsCancelled(false);
            setShowCompletedBanner(false);
          }
          setShowDebug(false);
        }} className={`w-9 h-5 rounded-full transition-colors relative ${isMissed ? 'bg-emerald-500' : 'bg-slate-600'}`} data-id="element-1255">
              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${isMissed ? 'translate-x-4' : 'translate-x-0.5'}`} data-id="element-1256" />
            </button>
          </div>
          <div className="flex items-center justify-between px-2 py-1.5" data-id="element-1257">
            <span className="text-slate-300 font-medium" data-id="element-1258">Future banner</span>
            <button onClick={() => {
          setShowFutureBanner(!showFutureBanner);
          setShowDebug(false);
        }} className={`w-9 h-5 rounded-full transition-colors relative ${showFutureBanner ? 'bg-emerald-500' : 'bg-slate-600'}`} data-id="element-1259">
              <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform ${showFutureBanner ? 'translate-x-4' : 'translate-x-0.5'}`} data-id="element-1260" />
            </button>
          </div>

          <div className="border-t border-slate-700 pt-2 mt-2 mb-1" data-id="element-1261">
            <p className="font-bold text-slate-400 uppercase tracking-wider text-[10px] mb-2" data-id="element-1262">
              Skip Exercise Flow
            </p>
            <button onClick={() => {
          setCorrectingExercise('Barbell Squat');
          setShowDebug(false);
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-1263">
              ▶ Open Edit exercise sheet
            </button>
            <button onClick={() => {
          handleSkipExercise('Barbell Squat');
          setShowDebug(false);
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-1264">
              ▶ Skip exercise (with toast)
            </button>
            <button onClick={() => {
          setSkippedExercise('Barbell Squat');
          setShowSkipToast(true);
          setShowDebug(false);
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium" data-id="element-1265">
              ▶ Show skip toast with Undo
            </button>
          </div>

          <div className="border-t border-slate-700 pt-2 mt-2 mb-1" data-id="element-1266">
            <p className="font-bold text-slate-400 uppercase tracking-wider text-[10px] mb-2" data-id="element-1267">
              Completed Workout State
            </p>
            <button onClick={() => {
          setShowCompletedBanner(true);
          setIsCancelled(false);
          setIsMissed(false);
          setShowFutureBanner(false);
          setShowDebug(false);
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium mb-1" data-id="element-1268">
              ▶ Show completed state
            </button>
            <button onClick={() => {
          setShowCompletedBanner(false);
          setShowDebug(false);
        }} className="w-full text-left px-2 py-1.5 rounded-lg hover:bg-slate-700 transition-colors font-medium" data-id="element-1269">
              ▶ Hide completed state
            </button>
          </div>
        </div>}

      {/* Header */}
      <div className="flex items-center justify-between p-6 pt-8 bg-stone-50/90 backdrop-blur-md z-20 shrink-0 border-b border-slate-100" data-id="element-1270">
        <button onClick={onBack} className="p-2 -ml-2 rounded-full hover:bg-slate-200/50 text-slate-600 transition-colors" aria-label="Go back" data-id="element-1271">
          <ChevronLeft size={28} data-id="element-1272" />
        </button>
        <div className="text-center" data-id="element-1273">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-0.5" data-id="element-1274">
            Today, Oct 14
          </p>
          <h1 className="text-lg font-extrabold text-slate-800" data-id="element-1275">
            Full Body Power
          </h1>
          {isCancelled && <span className="inline-block mt-1 px-2.5 py-0.5 bg-pink-50 text-pink-500 text-[10px] font-extrabold uppercase tracking-wider rounded-lg" data-id="element-1276">
              Cancelled
            </span>}
          {isMissed && <span className="inline-block mt-1 px-2.5 py-0.5 bg-amber-50 text-amber-500 text-[10px] font-extrabold uppercase tracking-wider rounded-lg" data-id="element-1277">
              Missed
            </span>}
          {showCompletedBanner && <span className="inline-block mt-1 px-2.5 py-0.5 bg-emerald-50 text-emerald-600 text-[10px] font-extrabold uppercase tracking-wider rounded-lg" data-id="element-1278">
              Completed
            </span>}
        </div>
        <div className="w-10" data-id="element-1279" /> {/* Spacer */}
      </div>

      {/* Future Workout Recalculation Banner */}
      {isFuturePreview && <div className="mx-6 mt-4 mb-4 flex items-start gap-2.5 bg-blue-50 border border-blue-100 rounded-2xl px-4 py-3 animate-in fade-in slide-in-from-top-2 duration-200 shrink-0" data-id="element-1280">
          <Info size={16} className="text-blue-400 shrink-0 mt-0.5" data-id="element-1281" />
          <p className="text-xs font-medium text-blue-600 leading-relaxed" data-id="element-1282">
            Planned values will be updated before this workout week starts,
            based on your previous week's results.
          </p>
        </div>}

      {/* Completed Workout Summary Banner */}
      {showCompletedBanner && <div className="mx-6 mt-4 mb-4 bg-white border border-emerald-100 rounded-3xl p-5 shadow-sm animate-in fade-in slide-in-from-top-2 duration-200 shrink-0" data-id="element-1283">
          <div className="flex items-center gap-2 mb-3" data-id="element-1284">
            <CheckCircle2 size={18} className="text-emerald-500" data-id="element-1285" />
            <h3 className="text-sm font-extrabold text-slate-800" data-id="element-1286">
              Workout completed
            </h3>
          </div>

          <p className="text-xs font-medium text-slate-600 leading-relaxed mb-4" data-id="element-1287">
            Nice work — you completed this session with{' '}
            <span className="font-bold text-slate-800" data-id="element-1288">12,460 kg</span> of total
            training volume.
          </p>

          <div className="flex items-center gap-4 pt-3 border-t border-slate-100" data-id="element-1289">
            <div className="flex items-center gap-1.5" data-id="element-1290">
              <Dumbbell size={14} className="text-emerald-500" data-id="element-1291" />
              <div className="text-xs" data-id="element-1292">
                <span className="font-bold text-slate-800" data-id="element-1293">18</span>
                <span className="text-slate-500 font-medium" data-id="element-1294"> sets</span>
              </div>
            </div>
            <div className="text-xs text-slate-500" data-id="element-1295">•</div>
            <div className="text-xs" data-id="element-1296">
              <span className="font-medium text-slate-500" data-id="element-1297">Primary: </span>
              <span className="font-bold text-slate-800" data-id="element-1298">Chest</span>
            </div>
            <div className="text-xs text-slate-500" data-id="element-1299">•</div>
            <div className="text-xs" data-id="element-1300">
              <span className="font-medium text-slate-500" data-id="element-1301">Secondary: </span>
              <span className="font-bold text-slate-800" data-id="element-1302">Triceps</span>
            </div>
          </div>
        </div>}

      {/* Scrollable Content */}
      <div className={`flex-1 overflow-y-auto px-6 ${isFuturePreview || showCompletedBanner ? 'pt-0' : 'pt-6'} space-y-4 ${isReadOnly ? 'pb-24' : 'pb-40'}`} data-id="element-1303">
        {exercises.map(ex => <div key={ex.id} className={`bg-white rounded-3xl p-5 shadow-sm border border-slate-100 ${isReadOnly && !showCompletedBanner ? 'opacity-80' : ''}`} data-id="element-1304">
            <div className="flex items-center justify-between mb-4" data-id="element-1305">
              <h2 className="text-lg font-extrabold text-slate-800" data-id="element-1306">
                {ex.name}
              </h2>
              {showCompletedBanner && <div className="w-6 h-6 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center" data-id="element-1307">
                  <Check size={14} strokeWidth={3} data-id="element-1308" />
                </div>}
            </div>

            {showCompletedBanner ? <>
                <div className="grid grid-cols-3 gap-2 mb-5" data-id="element-1309">
                  <div className="bg-emerald-50/50 rounded-xl p-2 text-center" data-id="element-1310">
                    <p className="text-[10px] font-bold text-emerald-600 uppercase mb-1" data-id="element-1311">
                      Weight
                    </p>
                    <p className="text-sm font-extrabold text-slate-700" data-id="element-1312">
                      {ex.completedWeight}
                    </p>
                  </div>
                  <div className="bg-emerald-50/50 rounded-xl p-2 text-center" data-id="element-1313">
                    <p className="text-[10px] font-bold text-emerald-600 uppercase mb-1" data-id="element-1314">
                      Sets
                    </p>
                    <p className="text-sm font-extrabold text-slate-700" data-id="element-1315">
                      {ex.completedSets}
                    </p>
                  </div>
                  <div className="bg-emerald-50/50 rounded-xl p-2 text-center" data-id="element-1316">
                    <p className="text-[10px] font-bold text-emerald-600 uppercase mb-1" data-id="element-1317">
                      Reps
                    </p>
                    <p className="text-sm font-extrabold text-slate-700" data-id="element-1318">
                      {ex.completedReps}
                    </p>
                  </div>
                </div>
              </> : <div className="grid grid-cols-4 gap-2 mb-5" data-id="element-1319">
                <div className="bg-stone-50 rounded-xl p-2 text-center" data-id="element-1320">
                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1" data-id="element-1321">
                    Target
                  </p>
                  <p className="text-sm font-extrabold text-slate-700" data-id="element-1322">
                    {ex.weight}
                  </p>
                </div>
                <div className="bg-stone-50 rounded-xl p-2 text-center" data-id="element-1323">
                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1" data-id="element-1324">
                    Sets
                  </p>
                  <p className="text-sm font-extrabold text-slate-700" data-id="element-1325">
                    {ex.sets}
                  </p>
                </div>
                <div className="bg-stone-50 rounded-xl p-2 text-center" data-id="element-1326">
                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1" data-id="element-1327">
                    Reps
                  </p>
                  <p className="text-sm font-extrabold text-slate-700" data-id="element-1328">
                    {ex.reps}
                  </p>
                </div>
                <div className="bg-stone-50 rounded-xl p-2 text-center" data-id="element-1329">
                  <p className="text-[10px] font-bold text-slate-400 uppercase mb-1" data-id="element-1330">
                    Rest
                  </p>
                  <p className="text-sm font-extrabold text-slate-700" data-id="element-1331">
                    {ex.rest}
                  </p>
                </div>
              </div>}

            <div className="flex items-center justify-between border-t border-slate-100 pt-4" data-id="element-1332">
              {isReadOnly ? <div className="w-full flex justify-center" data-id="element-1333">
                  <button onClick={() => onViewAnalytics(ex.name)} className="flex items-center gap-1.5 text-sm font-bold text-blue-500 hover:text-blue-600 transition-colors" data-id="element-1334">
                    <BarChart2 size={16} data-id="element-1335" />
                    Analytics
                  </button>
                </div> : <>
                  <button onClick={() => setCorrectingExercise(ex.name)} className="flex items-center gap-1.5 text-sm font-bold text-blue-500 hover:text-blue-600 transition-colors" data-id="element-1336">
                    <Edit2 size={16} data-id="element-1337" />
                    Edit
                  </button>
                  <button onClick={() => onViewAnalytics(ex.name)} className="flex items-center gap-1.5 text-sm font-bold text-blue-500 hover:text-blue-600 transition-colors" data-id="element-1338">
                    <BarChart2 size={16} data-id="element-1339" />
                    Progress
                  </button>
                  <button onClick={() => setReplacingExercise(ex.name)} className="flex items-center gap-1.5 text-sm font-bold text-slate-400 hover:text-slate-600 transition-colors" data-id="element-1340">
                    <ArrowRightLeft size={16} data-id="element-1341" />
                    Replace
                  </button>
                </>}
            </div>
          </div>)}
      </div>

      {/* Sticky Footer - hidden when read-only */}
      {!isReadOnly && <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-stone-50 via-stone-50 to-transparent pt-12 z-20 pointer-events-none" data-id="element-1342">
          <div className="pointer-events-auto flex flex-col gap-3" data-id="element-1343">
            <Button variant="primary" fullWidth onClick={() => setShowCompleteModal(true)} data-id="element-1344">
              Complete workout
            </Button>
            <button onClick={() => setShowChangeDateModal(true)} className="flex items-center justify-center gap-2 text-sm font-bold text-slate-600 hover:text-slate-800 py-2 transition-colors" data-id="element-1345">
              <Calendar size={16} data-id="element-1346" />
              Change target date
            </button>
          </div>
        </div>}

      {/* Modals */}
      {replacingExercise && <ReplaceExerciseSheet currentExercise={replacingExercise} onConfirm={newName => handleReplace(replacingExercise, newName)} onClose={() => setReplacingExercise(null)} data-id="element-1347" />}

      {correctingExercise && <CorrectExerciseSheet exerciseName={correctingExercise} onSave={() => setCorrectingExercise(null)} onClose={() => setCorrectingExercise(null)} onSkip={() => handleSkipExercise(correctingExercise)} data-id="element-1348" />}

      {showCompleteModal && <CompleteWorkoutModal onConfirm={() => {
      setShowCompleteModal(false);
      onComplete();
    }} onCancel={() => setShowCompleteModal(false)} data-id="element-1349" />}

      {showChangeDateModal && <ChangeTargetDateModal onConfirm={date => {
      setShowChangeDateModal(false);
      onBack();
    }} onCancel={() => setShowChangeDateModal(false)} data-id="element-1350" />}

      {/* Skip Toast Notification */}
      {showSkipToast && skippedExercise && <div className="absolute bottom-24 left-6 right-6 z-50 animate-in slide-in-from-bottom-4 fade-in duration-300" data-id="element-1351">
          <div className="bg-slate-800 text-white rounded-2xl p-4 shadow-2xl flex items-center justify-between" data-id="element-1352">
            <p className="text-sm font-medium" data-id="element-1353">
              <span className="font-bold" data-id="element-1354">{skippedExercise}</span> skipped for
              today
            </p>
            <button onClick={handleUndoSkip} className="text-sm font-bold text-emerald-400 hover:text-emerald-300 transition-colors ml-4" data-id="element-1355">
              Undo
            </button>
          </div>
        </div>}
    </div>;
}